import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Plus, Trash2, Edit, Camera, CheckCircle, Calendar,
  Clock, ChevronDown, ChevronUp, Search, DollarSign, Edit2, Download, FileText, FileSpreadsheet
} from 'lucide-react';
import FaceCapture from './FaceCapture';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialFormState = {
  full_name: '', age: '', phone_number: '', sex: 'Male', aadhaar_number: '',
  address: '', city: '', pincode: '', face_descriptor: null, profile_photo: null,
  monthly_salary: '', working_hours_per_day: '8', ot_fine_frequency: '1',
  auto_punch_out_time: '', paid_leaves_per_month: '0', retain_leaves_add_money: false
};

const StaffManagement = ({ franchiseId }) => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddStaffOpen, setIsAddStaffOpen] = useState(false);
  const [isEditStaffOpen, setIsEditStaffOpen] = useState(false);
  const [isFaceCaptureOpen, setIsFaceCaptureOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [salaryData, setSalaryData] = useState(null);
  const [editingPunch, setEditingPunch] = useState(null);
  const [pinForEdit, setPinForEdit] = useState('');
  const [newPunchTime, setNewPunchTime] = useState('');
  const [staffForm, setStaffForm] = useState(initialFormState);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchStaff(); }, [franchiseId]);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff?franchise_id=${franchiseId}`);
      setStaffList(response.data.staff || []);
    } catch (error) { toast.error('Failed to load staff'); }
  };

  const fetchStaffSalary = async (staffId) => {
    try {
      const now = new Date();
      const response = await axios.get(`${API}/staff/${staffId}/salary?month=${now.getMonth() + 1}&year=${now.getFullYear()}`);
      setSalaryData(response.data);
    } catch (error) { console.error('Error fetching salary:', error); }
  };

  const fetchStaffAttendance = async (staffId) => {
    try {
      const response = await axios.get(`${API}/attendance?franchise_id=${franchiseId}&staff_id=${staffId}&start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`);
      setAttendanceData(response.data.attendance || []);
    } catch (error) { toast.error('Failed to load attendance'); }
  };

  const handleFormChange = (field, value) => {
    setStaffForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.face_descriptor) { toast.error('Please register face before saving'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/staff`, {
        franchise_id: franchiseId, full_name: staffForm.full_name, age: parseInt(staffForm.age),
        phone_number: staffForm.phone_number, sex: staffForm.sex, aadhaar_number: staffForm.aadhaar_number,
        address: staffForm.address, city: staffForm.city, pincode: staffForm.pincode,
        face_descriptor: staffForm.face_descriptor, profile_photo: staffForm.profile_photo,
        monthly_salary: parseFloat(staffForm.monthly_salary) || 0,
        working_hours_per_day: parseFloat(staffForm.working_hours_per_day) || 8,
        ot_fine_frequency: parseFloat(staffForm.ot_fine_frequency) || 1,
        auto_punch_out_time: staffForm.auto_punch_out_time || null,
        paid_leaves_per_month: parseInt(staffForm.paid_leaves_per_month) || 0,
        retain_leaves_add_money: staffForm.retain_leaves_add_money
      });
      toast.success('Staff added successfully!');
      setIsAddStaffOpen(false);
      setStaffForm(initialFormState);
      fetchStaff();
    } catch (error) { toast.error('Failed to add staff'); }
    finally { setLoading(false); }
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/staff/${selectedStaff.staff_id}`, {
        full_name: staffForm.full_name, age: parseInt(staffForm.age), phone_number: staffForm.phone_number,
        sex: staffForm.sex, aadhaar_number: staffForm.aadhaar_number, address: staffForm.address,
        city: staffForm.city, pincode: staffForm.pincode, face_descriptor: staffForm.face_descriptor,
        profile_photo: staffForm.profile_photo,
        monthly_salary: parseFloat(staffForm.monthly_salary) || 0,
        working_hours_per_day: parseFloat(staffForm.working_hours_per_day) || 8,
        ot_fine_frequency: parseFloat(staffForm.ot_fine_frequency) || 1,
        auto_punch_out_time: staffForm.auto_punch_out_time || null,
        paid_leaves_per_month: parseInt(staffForm.paid_leaves_per_month) || 0,
        retain_leaves_add_money: staffForm.retain_leaves_add_money
      });
      toast.success('Staff updated successfully!');
      setIsEditStaffOpen(false);
      setStaffForm(initialFormState);
      setSelectedStaff(null);
      fetchStaff();
    } catch (error) { toast.error('Failed to update staff'); }
    finally { setLoading(false); }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await axios.delete(`${API}/staff/${staffId}`);
      toast.success('Staff deleted successfully!');
      fetchStaff();
    } catch (error) { toast.error('Failed to delete staff'); }
  };

  const handleFaceCapture = (data) => {
    setStaffForm(prev => ({ ...prev, face_descriptor: data.descriptor, profile_photo: data.image }));
    setIsFaceCaptureOpen(false);
    toast.success('Face registered successfully!');
  };

  const handleEditPunchTime = async () => {
    if (!pinForEdit || !newPunchTime) { toast.error('Please enter PIN and new time'); return; }
    try {
      await axios.put(`${API}/attendance/${editingPunch.attendance_id}?franchise_id=${franchiseId}`, { punch_time: newPunchTime, pin: pinForEdit });
      toast.success('Punch time updated successfully!');
      setEditingPunch(null); setPinForEdit(''); setNewPunchTime('');
      if (expandedStaff) { fetchStaffAttendance(expandedStaff); fetchStaffSalary(expandedStaff); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update punch time'); }
  };

  const openEditDialog = (staff) => {
    setSelectedStaff(staff);
    setStaffForm({
      full_name: staff.full_name || '', age: staff.age?.toString() || '', phone_number: staff.phone_number || '',
      sex: staff.sex || 'Male', aadhaar_number: staff.aadhaar_number || '', address: staff.address || '',
      city: staff.city || '', pincode: staff.pincode || '', face_descriptor: staff.face_descriptor,
      profile_photo: staff.profile_photo || null,
      monthly_salary: staff.monthly_salary?.toString() || '', working_hours_per_day: staff.working_hours_per_day?.toString() || '8',
      ot_fine_frequency: staff.ot_fine_frequency?.toString() || '1', auto_punch_out_time: staff.auto_punch_out_time || '',
      paid_leaves_per_month: staff.paid_leaves_per_month?.toString() || '0', retain_leaves_add_money: staff.retain_leaves_add_money || false
    });
    setIsEditStaffOpen(true);
  };

  const toggleStaffExpand = async (staff) => {
    if (expandedStaff === staff.staff_id) { setExpandedStaff(null); setAttendanceData([]); setSalaryData(null); }
    else { setExpandedStaff(staff.staff_id); await fetchStaffAttendance(staff.staff_id); await fetchStaffSalary(staff.staff_id); }
  };

  // Generate PDF Report
  const generatePDFReport = (staff) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(62, 39, 35);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Staff Attendance Report', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`${dateRange.start_date} to ${dateRange.end_date}`, pageWidth / 2, 25, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    let y = 45;
    
    // Staff Details
    doc.setFillColor(240, 240, 240);
    doc.rect(10, y - 5, pageWidth - 20, 30, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Details', 15, y + 3);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${staff.full_name}`, 15, y + 12);
    doc.text(`Phone: ${staff.phone_number}`, 15, y + 19);
    doc.text(`Monthly Salary: ₹${staff.monthly_salary || 0}`, 100, y + 12);
    doc.text(`Working Hours/Day: ${staff.working_hours_per_day || 8}h`, 100, y + 19);
    y += 40;
    
    // Summary Section
    if (salaryData) {
      doc.setFillColor(232, 245, 233);
      doc.rect(10, y - 5, pageWidth - 20, 35, 'F');
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Attendance Summary', 15, y + 3);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      const summary = salaryData.summary;
      doc.setTextColor(0, 128, 0);
      doc.text(`Present: ${summary?.days_present || 0} days`, 15, y + 14);
      doc.setTextColor(255, 0, 0);
      doc.text(`Absent: ${summary?.days_absent || 0} days`, 60, y + 14);
      doc.setTextColor(0, 0, 255);
      doc.text(`Overtime: ${summary?.total_ot_hours || 0}h`, 105, y + 14);
      doc.setTextColor(255, 140, 0);
      doc.text(`Fine Hours: ${summary?.total_fine_hours || 0}h`, 150, y + 14);
      
      doc.setTextColor(0, 0, 0);
      doc.text(`Paid Leaves Used: ${summary?.paid_leaves_used || 0}`, 15, y + 24);
      doc.text(`Unpaid Absents: ${summary?.unpaid_absent_days || 0}`, 80, y + 24);
      doc.setFont('helvetica', 'bold');
      doc.text(`Final Salary: ₹${salaryData.calculation?.final_salary?.toFixed(2) || 0}`, 145, y + 24);
      y += 45;
    }
    
    // Attendance Details Table
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Daily Attendance Details', 15, y);
    y += 8;
    
    // Table Header
    doc.setFillColor(62, 39, 35);
    doc.rect(10, y - 4, pageWidth - 20, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Date', 15, y + 2);
    doc.text('Status', 45, y + 2);
    doc.text('Punch In', 75, y + 2);
    doc.text('Punch Out', 105, y + 2);
    doc.text('Hours', 140, y + 2);
    doc.text('OT/Fine', 165, y + 2);
    y += 12;
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    
    // Sort dates
    const sortedDates = Object.keys(groupedAttendance).sort((a, b) => a.localeCompare(b));
    
    sortedDates.forEach((date, idx) => {
      if (y > 270) { doc.addPage(); y = 20; }
      
      const records = groupedAttendance[date];
      const punchIn = records.find(r => r.punch_type === 'in');
      const punchOut = records.find(r => r.punch_type === 'out');
      
      // Calculate hours
      let workedHours = 0;
      if (punchIn && punchOut) {
        try {
          const timeIn = new Date(`2000-01-01T${punchIn.time}`);
          const timeOut = new Date(`2000-01-01T${punchOut.time}`);
          workedHours = ((timeOut - timeIn) / 1000 / 60 / 60).toFixed(2);
        } catch (e) {}
      }
      
      const expectedHours = staff.working_hours_per_day || 8;
      const otHours = workedHours > expectedHours ? (workedHours - expectedHours).toFixed(2) : 0;
      const fineHours = workedHours < expectedHours && workedHours > 0 ? (expectedHours - workedHours).toFixed(2) : 0;
      
      // Alternating row colors
      if (idx % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(10, y - 4, pageWidth - 20, 8, 'F');
      }
      
      doc.setFontSize(9);
      doc.text(date, 15, y);
      doc.setTextColor(punchIn ? 0 : 255, punchIn ? 128 : 0, 0);
      doc.text(punchIn ? 'Present' : 'Absent', 45, y);
      doc.setTextColor(0, 0, 0);
      doc.text(punchIn?.time || '-', 75, y);
      doc.text(punchOut?.time || '-', 105, y);
      doc.text(workedHours > 0 ? `${workedHours}h` : '-', 140, y);
      
      if (otHours > 0) {
        doc.setTextColor(0, 0, 255);
        doc.text(`+${otHours}h OT`, 165, y);
      } else if (fineHours > 0) {
        doc.setTextColor(255, 140, 0);
        doc.text(`-${fineHours}h Fine`, 165, y);
      } else {
        doc.setTextColor(0, 0, 0);
        doc.text('-', 165, y);
      }
      doc.setTextColor(0, 0, 0);
      y += 8;
    });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, 290, { align: 'center' });
    
    doc.save(`${staff.full_name}_Attendance_${dateRange.start_date}_to_${dateRange.end_date}.pdf`);
    toast.success('PDF Report downloaded!');
  };

  // Generate Excel Report
  const generateExcelReport = (staff) => {
    // Summary Sheet Data
    const summaryData = [
      ['STAFF ATTENDANCE REPORT'],
      [''],
      ['Employee Details'],
      ['Name', staff.full_name],
      ['Phone', staff.phone_number],
      ['Monthly Salary', `₹${staff.monthly_salary || 0}`],
      ['Working Hours/Day', `${staff.working_hours_per_day || 8}h`],
      ['Report Period', `${dateRange.start_date} to ${dateRange.end_date}`],
      [''],
      ['Attendance Summary'],
      ['Days Present', salaryData?.summary?.days_present || 0],
      ['Days Absent', salaryData?.summary?.days_absent || 0],
      ['Total Overtime Hours', `${salaryData?.summary?.total_ot_hours || 0}h`],
      ['Total Fine Hours', `${salaryData?.summary?.total_fine_hours || 0}h`],
      ['Paid Leaves Used', salaryData?.summary?.paid_leaves_used || 0],
      ['Unpaid Absent Days', salaryData?.summary?.unpaid_absent_days || 0],
      [''],
      ['Salary Calculation'],
      ['Base Salary', `₹${salaryData?.calculation?.base_salary?.toFixed(2) || 0}`],
      ['Overtime Amount', `₹${salaryData?.calculation?.ot_amount?.toFixed(2) || 0}`],
      ['Fine Deduction', `₹${salaryData?.calculation?.fine_deduction?.toFixed(2) || 0}`],
      ['Absent Deduction', `₹${salaryData?.calculation?.absent_deduction?.toFixed(2) || 0}`],
      ['Leave Bonus', `₹${salaryData?.calculation?.leave_bonus?.toFixed(2) || 0}`],
      ['Final Salary', `₹${salaryData?.calculation?.final_salary?.toFixed(2) || 0}`],
    ];
    
    // Daily Attendance Sheet Data
    const attendanceHeaders = ['Date', 'Status', 'Punch In', 'Punch Out', 'Hours Worked', 'Overtime', 'Fine Hours', 'Remarks'];
    const attendanceRows = [];
    
    const sortedDates = Object.keys(groupedAttendance).sort((a, b) => a.localeCompare(b));
    const expectedHours = staff.working_hours_per_day || 8;
    
    sortedDates.forEach(date => {
      const records = groupedAttendance[date];
      const punchIn = records.find(r => r.punch_type === 'in');
      const punchOut = records.find(r => r.punch_type === 'out');
      
      let workedHours = 0;
      if (punchIn && punchOut) {
        try {
          const timeIn = new Date(`2000-01-01T${punchIn.time}`);
          const timeOut = new Date(`2000-01-01T${punchOut.time}`);
          workedHours = ((timeOut - timeIn) / 1000 / 60 / 60);
        } catch (e) {}
      }
      
      const otHours = workedHours > expectedHours ? workedHours - expectedHours : 0;
      const fineHours = workedHours < expectedHours && workedHours > 0 ? expectedHours - workedHours : 0;
      
      let remarks = '';
      if (otHours > 0) remarks = 'Overtime';
      else if (fineHours > 0) remarks = 'Short hours';
      else if (!punchIn) remarks = 'Absent';
      else remarks = 'On time';
      
      attendanceRows.push([
        date,
        punchIn ? 'Present' : 'Absent',
        punchIn?.time || '-',
        punchOut?.time || '-',
        workedHours > 0 ? workedHours.toFixed(2) : 0,
        otHours > 0 ? otHours.toFixed(2) : 0,
        fineHours > 0 ? fineHours.toFixed(2) : 0,
        remarks
      ]);
    });
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Summary Sheet
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 20 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
    
    // Attendance Sheet
    const attendanceSheet = XLSX.utils.aoa_to_sheet([attendanceHeaders, ...attendanceRows]);
    attendanceSheet['!cols'] = [
      { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, 
      { wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(wb, attendanceSheet, 'Daily Attendance');
    
    // Download
    XLSX.writeFile(wb, `${staff.full_name}_Attendance_${dateRange.start_date}_to_${dateRange.end_date}.xlsx`);
    toast.success('Excel Report downloaded!');
  };

  const filteredStaff = staffList.filter(staff => staff.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || staff.phone_number?.includes(searchQuery));
  const groupedAttendance = attendanceData.reduce((acc, record) => { if (!acc[record.date]) acc[record.date] = []; acc[record.date].push(record); return acc; }, {});

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl font-bold text-primary flex items-center gap-2"><Users className="h-5 w-5" /> Staff Management</h2>
        <Button onClick={() => { setStaffForm(initialFormState); setIsAddStaffOpen(true); }} className="bg-primary text-white"><Plus className="h-4 w-4 mr-2" /> Add Staff</Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input placeholder="Search by name or phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
      </div>

      <div className="space-y-3">
        {filteredStaff.length === 0 ? (
          <Card><CardContent className="py-8 text-center"><Users className="h-12 w-12 mx-auto text-gray-300 mb-3" /><p className="text-secondary">No staff members registered</p></CardContent></Card>
        ) : (
          filteredStaff.map((staff) => (
            <Card key={staff.staff_id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50" onClick={() => toggleStaffExpand(staff)}>
                  <div className="flex items-center gap-3">
                    {staff.profile_photo ? (
                      <img src={staff.profile_photo} alt={staff.full_name} className="w-10 h-10 rounded-full object-cover border-2 border-green-500" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${staff.face_descriptor ? 'bg-green-500' : 'bg-gray-400'}`}>{staff.full_name?.charAt(0).toUpperCase()}</div>
                    )}
                    <div><p className="font-semibold text-primary">{staff.full_name}</p><p className="text-sm text-secondary">{staff.phone_number} • ₹{staff.monthly_salary || 0}/month</p></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditDialog(staff); }}><Edit className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteStaff(staff.staff_id); }}><Trash2 className="h-4 w-4" /></Button>
                    {expandedStaff === staff.staff_id ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </div>
                </div>
                {expandedStaff === staff.staff_id && (
                  <div className="border-t bg-muted/30 p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-4 p-2 bg-white rounded">
                      <div><span className="text-secondary">Hours/Day:</span> <span className="font-medium">{staff.working_hours_per_day || 8}h</span></div>
                      <div><span className="text-secondary">OT/Fine:</span> <span className="font-medium">{staff.ot_fine_frequency || 1}x</span></div>
                      <div><span className="text-secondary">Paid Leaves:</span> <span className="font-medium">{staff.paid_leaves_per_month || 0}/month</span></div>
                      <div><span className="text-secondary">Auto Punch-Out:</span> <span className="font-medium">{staff.auto_punch_out_time || 'Not set'}</span></div>
                    </div>
                    {salaryData && (
                      <div className="mb-4 p-3 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><DollarSign className="h-4 w-4" /> This Month&apos;s Summary</h4>
                        <div className="grid grid-cols-3 gap-3 text-center mb-3">
                          <div className="bg-green-100 rounded-lg p-2">
                            <p className="text-2xl font-bold text-green-700">{salaryData.summary?.days_present || 0}</p>
                            <p className="text-xs text-green-600">Present</p>
                          </div>
                          <div className="bg-red-100 rounded-lg p-2">
                            <p className="text-2xl font-bold text-red-700">{salaryData.summary?.days_absent || 0}</p>
                            <p className="text-xs text-red-600">Absent</p>
                          </div>
                          <div className="bg-blue-100 rounded-lg p-2">
                            <p className="text-2xl font-bold text-blue-700">{salaryData.summary?.total_ot_hours || 0}h</p>
                            <p className="text-xs text-blue-600">Overtime</p>
                          </div>
                        </div>
                        {salaryData.summary?.total_fine_hours > 0 && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mb-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-orange-700">Fine Hours (Short working)</span>
                              <span className="font-bold text-orange-700">{salaryData.summary?.total_fine_hours}h</span>
                            </div>
                            <p className="text-xs text-orange-600 mt-1">Accumulated from days with less than {staff.working_hours_per_day || 8}h worked</p>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2 text-xs border-t pt-2">
                          <div className="flex justify-between"><span className="text-secondary">Paid Leaves Used:</span><span className="font-medium">{salaryData.summary?.paid_leaves_used || 0}</span></div>
                          <div className="flex justify-between"><span className="text-secondary">Unpaid Absents:</span><span className="font-medium text-red-600">{salaryData.summary?.unpaid_absent_days || 0}</span></div>
                        </div>
                        <div className="mt-3 pt-2 border-t flex justify-between items-center">
                          <span className="text-sm font-medium">Final Salary:</span>
                          <span className="text-xl font-bold text-green-700">₹{salaryData.calculation?.final_salary?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                          <div className="flex flex-wrap gap-2">
                            <span>Base: ₹{salaryData.calculation?.base_salary?.toFixed(2) || 0}</span>
                            <span className="text-blue-600">+OT: ₹{salaryData.calculation?.ot_amount?.toFixed(2) || 0}</span>
                            {salaryData.calculation?.fine_deduction > 0 && <span className="text-orange-600">-Fine: ₹{salaryData.calculation?.fine_deduction?.toFixed(2) || 0}</span>}
                            {salaryData.calculation?.absent_deduction > 0 && <span className="text-red-600">-Absent: ₹{salaryData.calculation?.absent_deduction?.toFixed(2) || 0}</span>}
                            {salaryData.calculation?.leave_bonus > 0 && <span className="text-green-600">+Leave: ₹{salaryData.calculation?.leave_bonus?.toFixed(2) || 0}</span>}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Input type="date" value={dateRange.start_date} onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))} className="flex-1 min-w-[120px]" />
                      <Input type="date" value={dateRange.end_date} onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))} className="flex-1 min-w-[120px]" />
                      <Button size="sm" onClick={() => { fetchStaffAttendance(staff.staff_id); fetchStaffSalary(staff.staff_id); }}>Filter</Button>
                    </div>
                    
                    {/* Download Report Buttons */}
                    <div className="flex flex-wrap gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="w-full mb-2">
                        <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                          <Download className="h-4 w-4" /> Download Attendance Report
                        </p>
                        <p className="text-xs text-blue-600">Generate detailed report for selected date range</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => generatePDFReport(staff)}
                        className="flex-1 min-w-[100px] border-red-300 text-red-700 hover:bg-red-50"
                      >
                        <FileText className="h-4 w-4 mr-2" /> PDF Report
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => generateExcelReport(staff)}
                        className="flex-1 min-w-[100px] border-green-300 text-green-700 hover:bg-green-50"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel Report
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="font-semibold text-sm flex items-center gap-2"><Calendar className="h-4 w-4" /> Attendance Records</p>
                      {Object.keys(groupedAttendance).length === 0 ? (<p className="text-sm text-secondary py-2">No attendance records</p>) : (
                        Object.entries(groupedAttendance).sort(([a], [b]) => b.localeCompare(a)).map(([date, records]) => (
                          <div key={date} className="bg-white rounded p-2 text-sm">
                            <p className="font-medium text-primary mb-1">{date}</p>
                            <div className="flex flex-wrap gap-2">
                              {records.sort((a, b) => a.punch_time?.localeCompare(b.punch_time)).map((record, idx) => (
                                <div key={idx} className="flex items-center gap-1">
                                  <span className={`px-2 py-1 rounded text-xs ${record.punch_type === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    <Clock className="h-3 w-3 inline mr-1" />{record.punch_type?.toUpperCase()}: {record.time}{record.manually_edited && <span className="ml-1 text-yellow-600">*</span>}
                                  </span>
                                  <button onClick={() => { setEditingPunch(record); setNewPunchTime(record.time?.substring(0, 5) || ''); }} className="p-1 hover:bg-gray-100 rounded"><Edit2 className="h-3 w-3 text-gray-400" /></button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Staff Dialog */}
      <Dialog open={isAddStaffOpen} onOpenChange={setIsAddStaffOpen}>
        <DialogContent className="bg-surface max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Staff</DialogTitle></DialogHeader>
          <form onSubmit={handleAddStaff} className="space-y-4">
            <div className="border-b pb-3"><h4 className="font-semibold text-sm text-secondary mb-3">Personal Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Full Name *</Label><Input value={staffForm.full_name} onChange={(e) => handleFormChange('full_name', e.target.value)} required /></div>
                <div><Label>Age *</Label><Input type="number" value={staffForm.age} onChange={(e) => handleFormChange('age', e.target.value)} required /></div>
                <div><Label>Sex *</Label><Select value={staffForm.sex} onValueChange={(v) => handleFormChange('sex', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent></Select></div>
                <div className="col-span-2"><Label>Phone Number *</Label><Input value={staffForm.phone_number} onChange={(e) => handleFormChange('phone_number', e.target.value)} required /></div>
                <div className="col-span-2"><Label>Aadhaar Number *</Label><Input value={staffForm.aadhaar_number} onChange={(e) => handleFormChange('aadhaar_number', e.target.value)} required /></div>
                <div className="col-span-2"><Label>Address *</Label><Input value={staffForm.address} onChange={(e) => handleFormChange('address', e.target.value)} required /></div>
                <div><Label>City *</Label><Input value={staffForm.city} onChange={(e) => handleFormChange('city', e.target.value)} required /></div>
                <div><Label>Pincode *</Label><Input value={staffForm.pincode} onChange={(e) => handleFormChange('pincode', e.target.value)} required /></div>
              </div>
            </div>
            <div className="border-b pb-3"><h4 className="font-semibold text-sm text-secondary mb-3">Salary & Working Hours</h4>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Monthly Salary (₹)</Label><Input type="number" value={staffForm.monthly_salary} onChange={(e) => handleFormChange('monthly_salary', e.target.value)} placeholder="10000" /></div>
                <div><Label>Working Hours/Day</Label><Input type="number" step="0.5" value={staffForm.working_hours_per_day} onChange={(e) => handleFormChange('working_hours_per_day', e.target.value)} placeholder="8" /></div>
                <div><Label>OT & Fine Frequency</Label><Select value={staffForm.ot_fine_frequency} onValueChange={(v) => handleFormChange('ot_fine_frequency', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="0.5">0.5x</SelectItem><SelectItem value="1">1x</SelectItem><SelectItem value="1.5">1.5x</SelectItem><SelectItem value="2">2x</SelectItem></SelectContent></Select></div>
                <div><Label>Auto Punch-Out Time</Label><Input type="time" value={staffForm.auto_punch_out_time} onChange={(e) => handleFormChange('auto_punch_out_time', e.target.value)} /></div>
              </div>
            </div>
            <div className="border-b pb-3"><h4 className="font-semibold text-sm text-secondary mb-3">Leave Settings</h4>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Paid Leaves/Month</Label><Input type="number" value={staffForm.paid_leaves_per_month} onChange={(e) => handleFormChange('paid_leaves_per_month', e.target.value)} placeholder="0" /></div>
                <div className="flex items-center gap-2 pt-6"><input type="checkbox" id="retain_leaves_add" checked={staffForm.retain_leaves_add_money} onChange={(e) => handleFormChange('retain_leaves_add_money', e.target.checked)} className="w-4 h-4" /><Label htmlFor="retain_leaves_add" className="text-xs">Add unused leave to salary?</Label></div>
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2"><Label>Face Registration *</Label>{staffForm.face_descriptor && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Registered</span>}</div>
              {staffForm.profile_photo && (
                <div className="mb-3 flex justify-center">
                  <img src={staffForm.profile_photo} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-green-500" />
                </div>
              )}
              <Button type="button" variant="outline" onClick={() => setIsFaceCaptureOpen(true)} className="w-full"><Camera className="h-4 w-4 mr-2" />{staffForm.face_descriptor ? 'Re-register Face' : 'Register Face'}</Button>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => { setIsAddStaffOpen(false); setStaffForm(initialFormState); }} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loading || !staffForm.face_descriptor} className="flex-1 bg-primary text-white">{loading ? 'Saving...' : 'Save Staff'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={isEditStaffOpen} onOpenChange={setIsEditStaffOpen}>
        <DialogContent className="bg-surface max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Staff</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateStaff} className="space-y-4">
            <div className="border-b pb-3"><h4 className="font-semibold text-sm text-secondary mb-3">Personal Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Full Name *</Label><Input value={staffForm.full_name} onChange={(e) => handleFormChange('full_name', e.target.value)} required /></div>
                <div><Label>Age *</Label><Input type="number" value={staffForm.age} onChange={(e) => handleFormChange('age', e.target.value)} required /></div>
                <div><Label>Sex *</Label><Select value={staffForm.sex} onValueChange={(v) => handleFormChange('sex', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent></Select></div>
                <div className="col-span-2"><Label>Phone Number *</Label><Input value={staffForm.phone_number} onChange={(e) => handleFormChange('phone_number', e.target.value)} required /></div>
                <div className="col-span-2"><Label>Aadhaar Number *</Label><Input value={staffForm.aadhaar_number} onChange={(e) => handleFormChange('aadhaar_number', e.target.value)} required /></div>
                <div className="col-span-2"><Label>Address *</Label><Input value={staffForm.address} onChange={(e) => handleFormChange('address', e.target.value)} required /></div>
                <div><Label>City *</Label><Input value={staffForm.city} onChange={(e) => handleFormChange('city', e.target.value)} required /></div>
                <div><Label>Pincode *</Label><Input value={staffForm.pincode} onChange={(e) => handleFormChange('pincode', e.target.value)} required /></div>
              </div>
            </div>
            <div className="border-b pb-3"><h4 className="font-semibold text-sm text-secondary mb-3">Salary & Working Hours</h4>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Monthly Salary (₹)</Label><Input type="number" value={staffForm.monthly_salary} onChange={(e) => handleFormChange('monthly_salary', e.target.value)} placeholder="10000" /></div>
                <div><Label>Working Hours/Day</Label><Input type="number" step="0.5" value={staffForm.working_hours_per_day} onChange={(e) => handleFormChange('working_hours_per_day', e.target.value)} placeholder="8" /></div>
                <div><Label>OT & Fine Frequency</Label><Select value={staffForm.ot_fine_frequency} onValueChange={(v) => handleFormChange('ot_fine_frequency', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="0.5">0.5x</SelectItem><SelectItem value="1">1x</SelectItem><SelectItem value="1.5">1.5x</SelectItem><SelectItem value="2">2x</SelectItem></SelectContent></Select></div>
                <div><Label>Auto Punch-Out Time</Label><Input type="time" value={staffForm.auto_punch_out_time} onChange={(e) => handleFormChange('auto_punch_out_time', e.target.value)} /></div>
              </div>
            </div>
            <div className="border-b pb-3"><h4 className="font-semibold text-sm text-secondary mb-3">Leave Settings</h4>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Paid Leaves/Month</Label><Input type="number" value={staffForm.paid_leaves_per_month} onChange={(e) => handleFormChange('paid_leaves_per_month', e.target.value)} placeholder="0" /></div>
                <div className="flex items-center gap-2 pt-6"><input type="checkbox" id="retain_leaves_edit" checked={staffForm.retain_leaves_add_money} onChange={(e) => handleFormChange('retain_leaves_add_money', e.target.checked)} className="w-4 h-4" /><Label htmlFor="retain_leaves_edit" className="text-xs">Add unused leave to salary?</Label></div>
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2"><Label>Face Registration</Label>{staffForm.face_descriptor && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Registered</span>}</div>
              {staffForm.profile_photo && (
                <div className="mb-3 flex justify-center">
                  <img src={staffForm.profile_photo} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-green-500" />
                </div>
              )}
              <Button type="button" variant="outline" onClick={() => setIsFaceCaptureOpen(true)} className="w-full"><Camera className="h-4 w-4 mr-2" />{staffForm.face_descriptor ? 'Re-register Face' : 'Register Face'}</Button>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => { setIsEditStaffOpen(false); setStaffForm(initialFormState); setSelectedStaff(null); }} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-primary text-white">{loading ? 'Saving...' : 'Update Staff'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Face Capture Dialog */}
      <Dialog open={isFaceCaptureOpen} onOpenChange={setIsFaceCaptureOpen}>
        <DialogContent className="bg-surface max-w-md p-0 overflow-hidden">
          <FaceCapture mode="register" onCapture={handleFaceCapture} onCancel={() => setIsFaceCaptureOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit Punch Time Dialog */}
      <Dialog open={!!editingPunch} onOpenChange={(open) => { if (!open) { setEditingPunch(null); setPinForEdit(''); setNewPunchTime(''); } }}>
        <DialogContent className="bg-surface max-w-sm">
          <DialogHeader><DialogTitle>Edit Punch Time</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Current Time</Label><p className="text-lg font-medium">{editingPunch?.time} ({editingPunch?.punch_type?.toUpperCase()})</p><p className="text-sm text-secondary">{editingPunch?.date}</p></div>
            <div><Label>New Time (HH:MM)</Label><Input type="time" value={newPunchTime} onChange={(e) => setNewPunchTime(e.target.value)} /></div>
            <div><Label>Enter PIN to Confirm</Label><Input type="password" value={pinForEdit} onChange={(e) => setPinForEdit(e.target.value)} placeholder="Enter franchise PIN" /></div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => { setEditingPunch(null); setPinForEdit(''); setNewPunchTime(''); }} className="flex-1">Cancel</Button>
              <Button onClick={handleEditPunchTime} className="flex-1 bg-primary text-white">Update Time</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
